/* assets/gfun-wallet-workspace.js — GFUN-WALLET-DISPLAY-RUNTIME-07 */
(function () {
  'use strict';
  /**
   * @typedef {{
   *   customerId: string,
   *   known: boolean,
   *   balance: number | null,
   *   source: string
   * }} GFunBalanceSnapshot
   */
  /**
   * @typedef {{
   *   version: string,
   *   init: (root: HTMLElement) => void,
   *   hydrateBalance: (root: HTMLElement) => boolean,
   *   updateBalance: (root: HTMLElement) => boolean,
   *   renderUnavailable: (root: HTMLElement, reason?: unknown) => void,
   *   syncBalances: () => void,
   *   syncUnavailable: (reason?: unknown) => void
   * }} GFunWalletDisplay
   */
  /**
   * @type {Window & {
   *   __GFUN_WALLET_WORKSPACE_VERSION__?: string,
   *   CUSTOMER_DATA?: {isLoggedIn?: boolean, id?: unknown} | null,
   *   GFunManager?: {
   *     getBalanceSnapshot?: () => GFunBalanceSnapshot,
   *     refreshBalance?: (force?: boolean) => Promise<number | null>
   *   } | null,
   *   GFunWalletWorkspace?: GFunWalletDisplay
   * }}
   */
  const walletWindow = window;
  const VERSION = 'GFUN-WALLET-DISPLAY-RUNTIME-07';
  if (walletWindow.__GFUN_WALLET_WORKSPACE_VERSION__ === VERSION) return;
  walletWindow.__GFUN_WALLET_WORKSPACE_VERSION__ = VERSION;
  var ROOT_SELECTOR = '[data-gfun-wallet-workspace]';

  /**
   * @param {ParentNode} scope
   * @param {(root: HTMLElement) => void} callback
   */
  function each(scope, callback) {
    if (scope instanceof HTMLElement && scope.matches(ROOT_SELECTOR)) callback(scope);
    scope.querySelectorAll(ROOT_SELECTOR).forEach(function (node) {
      if (node instanceof HTMLElement) callback(node);
    });
  }

  /** @param {HTMLElement} root */
  function customerKey(root) {
    const data = walletWindow.CUSTOMER_DATA;
    if (data) return data.isLoggedIn === true ? String(data.id || '') : '';
    return root.dataset.gfunAuthenticated === 'true' ? root.dataset.gfunCustomerId || '' : '';
  }

  /**
   * @param {HTMLElement} root
   * @param {unknown} [reason]
   */
  function renderUnavailable(root, reason) {
    root.removeAttribute('data-gfun-balance');
    root.dataset.gfunBalanceState = 'unavailable';
    root.dataset.gfunBalanceReason = String(reason || 'BALANCE_NOT_VERIFIED');
    root.dataset.gfunBalanceSource = '';
    root.querySelectorAll('[data-gww-balance]').forEach(function (node) {
      if (!(node instanceof HTMLElement)) return;
      node.textContent = '—';
      node.dataset.gfunBalanceState = 'unavailable';
    });
  }

  /**
   * @param {HTMLElement} root
   * @param {number | null} value
   * @param {string} source
   */
  function renderReady(root, value, source) {
    root.dataset.gfunBalance = String(value);
    root.dataset.gfunBalanceState = 'ready';
    root.dataset.gfunBalanceSource = source;
    delete root.dataset.gfunBalanceReason;
    root.querySelectorAll('[data-gww-balance]').forEach(function (node) {
      if (!(node instanceof HTMLElement)) return;
      node.textContent = String(value);
      node.dataset.gfunBalanceState = 'ready';
    });
  }

  /** @param {HTMLElement} root */
  function hydrateBalance(root) {
    var customerId = customerKey(root);
    if (!customerId) {
      renderUnavailable(root, 'CUSTOMER_NOT_AUTHENTICATED');
      return false;
    }
    const manager = walletWindow.GFunManager;
    if (manager && typeof manager.getBalanceSnapshot === 'function') {
      var state = manager.getBalanceSnapshot();
      if (!state.known || state.customerId !== customerId) {
        renderUnavailable(root, 'BALANCE_NOT_VERIFIED');
        return false;
      }
      renderReady(root, state.balance, state.source);
      return true;
    }
    var raw = root.getAttribute('data-gfun-balance');
    if (
      root.dataset.gfunBalanceKnown !== 'true' ||
      root.dataset.gfunCustomerId !== customerId ||
      raw === null ||
      !/^\d+(?:\.\d+)?$/.test(raw) ||
      !Number.isFinite(Number(raw))
    ) {
      renderUnavailable(root, 'SERVER_BALANCE_MISSING');
      return false;
    }
    renderReady(root, Number(raw), 'server-rendered-metafield');
    return true;
  }

  /** @param {HTMLElement} root */
  function init(root) {
    if (root.dataset.gfunRuntimeReady === VERSION) return;
    root.dataset.gfunRuntimeReady = VERSION;
    hydrateBalance(root);
  }

  function boot(scope) {
    each(scope || document, init);
  }
  function syncBalances() {
    each(document, hydrateBalance);
  }
  function syncUnavailable(reason) {
    each(document, function (root) {
      renderUnavailable(root, reason);
    });
  }

  ['gfun:balance:update', 'gfun:balance-updated', 'gfun:updated'].forEach(function (name) {
    window.addEventListener(name, syncBalances);
  });
  window.addEventListener('gfun:unavailable', function (event) {
    const detail = 'detail' in event ? event.detail : null;
    const reason =
      detail !== null && typeof detail === 'object' && 'reason' in detail
        ? detail.reason
        : undefined;
    syncUnavailable(reason);
  });
  document.addEventListener('click', function (event) {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[data-gfun-wallet-refresh]');
    if (!(button instanceof HTMLButtonElement)) return;
    const root = button.closest(ROOT_SELECTOR);
    const manager = walletWindow.GFunManager;
    if (!(root instanceof HTMLElement)) return;
    if (!manager || typeof manager.refreshBalance !== 'function') {
      renderUnavailable(root, 'GFUN_MANAGER_UNAVAILABLE');
      return;
    }
    button.disabled = true;
    Promise.resolve(manager.refreshBalance(true))
      .then(function () {
        hydrateBalance(root);
      })
      .catch(function () {
        renderUnavailable(root, 'BALANCE_REFRESH_FAILED');
      })
      .finally(function () {
        button.disabled = false;
      });
  });
  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target || document);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    each(event.target || document, function (root) {
      delete root.dataset.gfunRuntimeReady;
    });
  });
  if (document.readyState === 'loading')
    document.addEventListener(
      'DOMContentLoaded',
      function () {
        boot(document);
      },
      { once: true }
    );
  else boot(document);
  walletWindow.GFunWalletWorkspace = {
    version: VERSION,
    init: init,
    hydrateBalance: hydrateBalance,
    updateBalance: hydrateBalance,
    renderUnavailable: renderUnavailable,
    syncBalances: syncBalances,
    syncUnavailable: syncUnavailable,
  };
})();
